import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { applyLoginSession } from "../authApi";
import { getStoredAccessToken } from "../authSession";
import { isSupabaseAuthConfigured, supabase } from "../lib/supabaseClient";
import { useToast } from "../components/ToastProvider";
import { Input } from "../components/ui/Input";
import Button from "../components/ui/Button";
import Logo from "../components/Logo";

function sanitizeNext(value) {
  const requested = String(value || "/dashboard").trim();
  if (!requested.startsWith("/")) return "/dashboard";
  if (requested === "/" || requested.startsWith("/login") || requested.startsWith("/auth/callback")) {
    return "/dashboard";
  }
  return requested;
}

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const [mode, setMode] = useState("signin"); // signin | signup | forgot
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const nextPath = sanitizeNext(location?.state?.from?.pathname || "");

  useEffect(() => {
    const stored = getStoredAccessToken();
    if (stored) {
      navigate(nextPath, { replace: true });
    }
  }, [navigate, nextPath]);

  function handleAuthMissingMessage() {
    return (
      <div className="rounded-lg p-3 text-sm bg-warn-subtle text-warn border border-warn/30">
        Supabase isn't configured yet. Add <span className="font-mono">VITE_SUPABASE_URL</span> and{" "}
        <span className="font-mono">VITE_SUPABASE_ANON_KEY</span> to enable sign-in.
      </div>
    );
  }

  async function handleGoogle() {
    if (!isSupabaseAuthConfigured || !supabase) return;
    setBusy(true);
    setError("");
    try {
      const redirectTo = `${window.location.origin}/?next=${encodeURIComponent(nextPath)}`;
      const { error: oauthError } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: { redirectTo, queryParams: { prompt: "select_account" } }
      });
      if (oauthError) throw oauthError;
    } catch (err) {
      setError(err.message || "Unable to start Google sign-in.");
      setBusy(false);
    }
  }

  async function handleEmailSubmit(e) {
    e.preventDefault();
    if (!isSupabaseAuthConfigured || !supabase) return;
    setError("");
    if (!email.includes("@") || password.length < 6) {
      setError("Enter a valid email and a password of at least 6 characters.");
      return;
    }
    setBusy(true);
    try {
      if (mode === "signup") {
        const { data, error: signError } = await supabase.auth.signUp({
          email,
          password,
          options: { emailRedirectTo: `${window.location.origin}/dashboard` }
        });
        if (signError) throw signError;
        if (data?.session) {
          applyLoginSession({
            accessToken: data.session.access_token,
            refreshToken: data.session.refresh_token,
            persist: true
          });
          toast.success("Account created.", "Welcome");
          navigate(nextPath, { replace: true });
        } else {
          toast.info("Check your inbox to confirm your email before signing in.", "Verify email");
          setMode("signin");
        }
      } else {
        const { data, error: signError } = await supabase.auth.signInWithPassword({
          email,
          password
        });
        if (signError) throw signError;
        applyLoginSession({
          accessToken: data.session.access_token,
          refreshToken: data.session.refresh_token,
          persist: true
        });
        toast.success("Welcome back.", "Signed in");
        navigate(nextPath, { replace: true });
      }
    } catch (err) {
      setError(err.message || "Authentication failed.");
    } finally {
      setBusy(false);
    }
  }

  async function handleForgot(e) {
    e.preventDefault();
    if (!isSupabaseAuthConfigured || !supabase) return;
    if (!email.includes("@")) {
      setError("Enter the email you signed up with.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/login`
      });
      if (resetError) throw resetError;
      toast.success("If that email is registered, a reset link is on its way.", "Sent");
      setMode("signin");
    } catch (err) {
      setError(err.message || "Could not send reset email.");
    } finally {
      setBusy(false);
    }
  }

  const isForgot = mode === "forgot";

  return (
    <div className="min-h-dvh grid place-items-center bg-surface text-ink p-4 sm:p-6">
      <div className="w-full max-w-md">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-ink-muted hover:text-ink transition-colors mb-6"
        >
          <span className="material-symbols-outlined text-[18px]">arrow_back</span>
          Back home
        </Link>

        <div className="obsidian-card overflow-hidden">
          <div className="px-6 sm:px-8 pt-8 pb-6 text-center">
            <div className="mx-auto h-12 w-12 rounded-2xl bg-brand-subtle text-brand grid place-items-center">
              <Logo className="w-6 h-6" />
            </div>
            <h1 className="font-display text-2xl font-bold mt-4">
              {isForgot
                ? "Reset your password"
                : mode === "signup"
                ? "Create your account"
                : "Welcome back"}
            </h1>
            <p className="text-sm text-ink-muted mt-1.5">
              {isForgot
                ? "We'll send you a reset link if the account exists."
                : "Track every hackathon you register for."}
            </p>
          </div>

          <div className="px-6 sm:px-8 pb-6 space-y-4">
            {!isSupabaseAuthConfigured && handleAuthMissingMessage()}

            {!isForgot && (
              <>
                <Button
                  variant="secondary"
                  className="w-full"
                  size="lg"
                  onClick={handleGoogle}
                  disabled={!isSupabaseAuthConfigured || busy}
                  iconStart="login"
                >
                  Continue with Google
                </Button>

                <div className="relative my-1">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-edge" />
                  </div>
                  <div className="relative flex justify-center text-[11px] uppercase tracking-widest">
                    <span className="px-2 bg-surface-raised text-ink-subtle">or</span>
                  </div>
                </div>
              </>
            )}

            <form onSubmit={isForgot ? handleForgot : handleEmailSubmit} className="space-y-3.5">
              <Input
                type="email"
                label="Email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                disabled={!isSupabaseAuthConfigured || busy}
                leftIcon="mail"
                required
              />
              {!isForgot && (
                <Input
                  type="password"
                  label={mode === "signup" ? "Create password" : "Password"}
                  placeholder="At least 6 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete={mode === "signup" ? "new-password" : "current-password"}
                  disabled={!isSupabaseAuthConfigured || busy}
                  leftIcon="lock"
                  required
                />
              )}

              {error && (
                <div className="rounded-lg p-3 text-xs bg-danger-subtle text-danger border border-danger/30">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="w-full"
                loading={busy}
                disabled={!isSupabaseAuthConfigured}
              >
                {isForgot ? "Send reset link" : mode === "signup" ? "Create account" : "Sign in"}
              </Button>
            </form>

            <div className="flex flex-col items-center gap-1.5 pt-1 text-sm">
              {!isForgot && mode === "signin" && (
                <button
                  type="button"
                  onClick={() => {
                    setError("");
                    setMode("forgot");
                  }}
                  className="text-ink-muted hover:text-ink underline-offset-2 hover:underline"
                >
                  Forgot password?
                </button>
              )}
              {isForgot ? (
                <button
                  type="button"
                  onClick={() => setMode("signin")}
                  className="text-ink-muted hover:text-ink"
                >
                  ← Back to sign in
                </button>
              ) : (
                <p className="text-ink-muted">
                  {mode === "signin" ? "New here?" : "Have an account?"}{" "}
                  <button
                    type="button"
                    onClick={() => {
                      setError("");
                      setMode(mode === "signin" ? "signup" : "signin");
                    }}
                    className="text-brand font-medium hover:underline"
                  >
                    {mode === "signin" ? "Create account" : "Sign in"}
                  </button>
                </p>
              )}
            </div>
          </div>

          <div className="px-6 sm:px-8 py-3 bg-surface-sunken/60 border-t border-edge text-center">
            <p className="text-[11px] text-ink-subtle">
              By continuing you agree to our terms. Your data stays private.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
